// src/prototype/components/ExplorerRow.tsx
// Ranked opportunity row in the Use Case Explorer. The whole row opens the decision
// brief; the compare checkbox is a stop-propagation island inside it.
import { annualBenefitShort } from '../roi/hubFigures';
import React from 'react';
import { Clock, ChevronRight, Link2, Plus, Check, Zap } from 'lucide-react';
import { EXPLORER_COLS } from './explorerColumns';
import { VerdictPill } from './VerdictPill';
import { EffortTagPill } from './EffortTagPill';
import type { DecisionBrief } from '../data/decisionBrief';

/** Why this opportunity surfaced (page-local context-signal shape). */
export interface RowSurfacedReason {
  contextLabel: string;
  contextValue: string;
  relevance: string;
}

export interface ExplorerRowProps {
  brief: DecisionBrief;
  onOpen: () => void;
  /** Tints the row when the use case is already in the Execute portfolio. */
  highlight?: boolean;
  /**
   * Row the view was asked to land on (an export returning to its own use case).
   * Deliberately NOT the same signal as `highlight`, which means "in the
   * portfolio" — a row can be both, and a returning user needs to see which one
   * they came back for, not just that it happens to be a finalist.
   */
  focused?: boolean;
  /** Scroll target id, so the page can bring this row into view by use case. */
  anchorId?: string;
  /** Omit compare props to render the row without a compare checkbox. */
  compareChecked?: boolean;
  onToggleCompare?: () => void;
  /** Disables the checkbox once the 3-item compare cap is reached. */
  compareDisabled?: boolean;
  /** "Why this surfaced" context signals, rendered as a muted line under the title. */
  surfacedReasons?: RowSurfacedReason[];
  /** Add/remove-to-Portfolio toggle. Omit to hide the button. Toggles finalist membership. */
  onTogglePortfolio?: () => void;
  /** Whether this use case is already in the portfolio (finalist). Drives the Added state. */
  isInPortfolio?: boolean;
  /** Freemium/locked: renders the button as an Upgrade affordance (click routes to /upgrade). */
  portfolioLocked?: boolean;
  /** Client commitment from the existing-initiatives upload (E2). Display only. */
  commitment?: { committed: boolean; percentComplete: number | null };
}

export const ExplorerRow: React.FC<ExplorerRowProps> = ({
  brief,
  onOpen,
  highlight = false,
  focused = false,
  anchorId,
  compareChecked,
  onToggleCompare,
  compareDisabled = false,
  surfacedReasons,
  onTogglePortfolio,
  isInPortfolio = false,
  portfolioLocked = false,
  commitment,
}) => {
  const topReason = surfacedReasons && surfacedReasons.length > 0 ? surfacedReasons[0] : null;
  const reasonTitle = surfacedReasons?.map((r) => `• ${r.relevance} (${r.contextLabel}: ${r.contextValue})`).join('\n');
  return (
  <div
    role="button"
    tabIndex={0}
    onClick={onOpen}
    onKeyDown={(e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onOpen();
      }
    }}
    data-explorer-row={anchorId}
    data-focused={focused ? 'true' : undefined}
    className={`${EXPLORER_COLS.track} py-3.5 border-b border-[#e5e7eb] cursor-pointer transition-colors group focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FF6E4C]/50 ${
      highlight ? 'bg-[#FFF1ED]' : 'bg-white hover:bg-[#F5F7FA]'
    } ${focused ? 'ring-2 ring-inset ring-[#FF6E4C] bg-[#FFF1ED]' : ''}`}
  >
    <div className={`${EXPLORER_COLS.rank} h-9 rounded-full bg-[#18365E] text-white font-bold text-sm flex items-center justify-center`}>
      {brief.rank}
    </div>

    <div className={EXPLORER_COLS.title}>
      <div className="flex items-center gap-2 flex-wrap">
        <h3 className="text-sm font-bold text-[#18365E] group-hover:text-[#FF6E4C] transition-colors">{brief.title}</h3>
        <VerdictPill verdict={brief.verdict} />
        {commitment?.committed && (
          <span className="px-2 py-0.5 text-[10px] font-semibold rounded-full bg-[#18365E] text-white">
            Committed{commitment.percentComplete != null ? ` · ${commitment.percentComplete}% complete` : ''}
          </span>
        )}
        {commitment && !commitment.committed && commitment.percentComplete != null && commitment.percentComplete > 0 && (
          <span className="px-2 py-0.5 text-[10px] font-medium rounded-full bg-[#F5F7FA] text-[#717182] border border-[#e5e7eb]">
            {commitment.percentComplete}% complete
          </span>
        )}
      </div>
      <span className="text-[10px] font-semibold text-[#717182] uppercase tracking-wide">{brief.category}</span>
      {topReason && (
        <p className="mt-0.5 flex items-center gap-1 text-[10px] text-[#717182] min-w-0" title={reasonTitle}>
          <Link2 className="w-3 h-3 shrink-0 text-[#18365E]/60" />
          <span className="truncate">
            Why surfaced: {topReason.relevance}
            {surfacedReasons && surfacedReasons.length > 1 && (
              <span className="font-semibold text-[#18365E]/70"> +{surfacedReasons.length - 1} more</span>
            )}
          </span>
        </p>
      )}
    </div>

    <div className={EXPLORER_COLS.value}>
      <span className="text-[10px] text-[#717182] block">Est. value</span>
      <span className="text-sm font-bold text-[#18365E]">
        {annualBenefitShort(brief.id)}
        <span className="text-[10px] font-medium text-[#717182]">/yr</span>
      </span>
    </div>

    <div className={`${EXPLORER_COLS.ttv} text-xs text-[#717182]`}>
      <span className="flex items-center gap-1.5">
        <Clock className="w-3.5 h-3.5 shrink-0" />
        {brief.ttv}
      </span>
    </div>

    <div className={EXPLORER_COLS.effort}>
      <EffortTagPill tag={brief.effortTag} />
    </div>

    <span className={EXPLORER_COLS.actions}>
      {onTogglePortfolio && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onTogglePortfolio(); }}
          title={portfolioLocked ? 'Upgrade to add to portfolio' : isInPortfolio ? 'Remove from Portfolio' : 'Add to Portfolio'}
          aria-label={isInPortfolio ? `Remove ${brief.title} from portfolio` : `Add ${brief.title} to portfolio`}
          className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-semibold transition-all shrink-0 ${
            portfolioLocked
              ? 'bg-[#e5e7eb] text-[#717182] hover:bg-[#d1d5db]'
              : isInPortfolio
                ? 'bg-[#FF6E4C] text-white shadow-sm hover:bg-[#FF6E4C]/90'
                : 'bg-[#F5F7FA] text-[#18365E] border border-[#e5e7eb] hover:bg-[#FF6E4C]/10 hover:border-[#FF6E4C]/30'
          }`}
        >
          {portfolioLocked ? (
            <><Zap className="w-3 h-3" />Upgrade</>
          ) : isInPortfolio ? (
            <><Check className="w-3 h-3" />Added</>
          ) : (
            <><Plus className="w-3 h-3" />Portfolio</>
          )}
        </button>
      )}
      {onToggleCompare && (
        <label
          onClick={(e) => e.stopPropagation()}
          className="flex items-center cursor-pointer"
          title={compareDisabled ? 'Compare is limited to 3 use cases' : 'Add to comparison'}
        >
          <input
            type="checkbox"
            checked={compareChecked}
            disabled={compareDisabled}
            onChange={onToggleCompare}
            aria-label={`Compare ${brief.title}`}
            className="accent-[#FF6E4C] w-4 h-4 disabled:opacity-40 disabled:cursor-not-allowed"
          />
        </label>
      )}
      <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#18365E] group-hover:text-[#FF6E4C] transition-colors">
        Open brief
        <ChevronRight className="w-3.5 h-3.5" />
      </span>
    </span>
  </div>
  );
};

export default ExplorerRow;
